import Phaser from 'phaser';
import { MetaManager, GAME_VERSION } from '../systems/MetaManager';
import { CampManager } from '../systems/CampManager';
import { SpriteRenderer } from '../rendering/SpriteRenderer';
import { RunManager } from '../systems/RunManager';
import { MusicManager, MUSIC_TRACKS } from '../systems/MusicManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { addHoverPulse } from '../ui/ButtonPolish';
import { PlayerProfile } from '../systems/PlayerProfile';
import { showUsernamePopup } from '../ui/UsernamePopup';
import { resetAllTutorials } from '../systems/TutorialResetter';
import { FONT_BODY } from '../utils/Typography';
import { hasPrepSnapshot, restoreFromPrepSnapshot, clearPrepSnapshot } from '../systems/PrepSnapshot';
import { shouldShowPatchNotesPopup, markPatchNotesAsSeen, getLastSeenVersion } from '../systems/PatchNotesTracker';
import { PATCH_NOTES } from '../data/patchNotes';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE D'ACCUEIL ===
// Crée les managers globaux, génère les textures SVG, et redirige vers le camp.
// Première visite : équipement starter offert.

const STARTER_KEY = 'deadtide_starter_given';

// Clés localStorage à effacer lors d'un reset complet (hors préférences musique)
const RESET_KEYS = [
  'deadtide_meta',
  'deadtide_camp',
  'deadtide_resources',
  'deadtide_expedition',
  'deadtide_upgrades',
  'deadtide_codex',
  'deadtide_run_save',
  'deadtide_prep_snapshot',
  'deadtide_starter_given',
  'deadtide_tutorial_done',
  'deadtide_combat_tuto_done',
  'deadtide_last_seen_version',
];

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Charger le logo — réduit légèrement sur mobile (66% écran → 56%)
    const logoW = GAME_W < 500 ? 250 : 300;
    const logoH = GAME_W < 500 ? 100 : 120;
    this.load.svg('logo', 'assets/images/logo.svg', { width: logoW, height: logoH });
    // Charger les pistes musicales
    for (const track of MUSIC_TRACKS) {
      this.load.audio(track.key, track.path);
    }
  }

  async create(): Promise<void> {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    const meta = new MetaManager();
    const camp = new CampManager();

    // === RÉCUPÉRATION ANTI-PERTE D'ITEMS (v0.38.17) ===
    // Si l'utilisateur a refresh pendant la prépa de run, l'inventaire en
    // mémoire (items pris dans le coffre) avait disparu — bug critique.
    // PrepSnapshot persiste l'état à chaque modif et BootScene le restaure
    // ici, AVANT que le joueur ne voie le menu. Si un run est déjà en cours
    // (saveRun existe), on ignore le snapshot : c'est de la donnée stale
    // depuis avant le démarrage du run.
    if (hasPrepSnapshot()) {
      if (RunManager.hasSavedRun()) {
        // Le run a déjà absorbé les items dans son save — snapshot obsolète.
        clearPrepSnapshot();
      } else {
        const restored = restoreFromPrepSnapshot(camp);
        if (restored.itemsLost > 0 || restored.consumablesLost > 0) {
          // Affiché plus tard si on veut, mais on ne bloque pas le boot.
          console.warn('[BootScene] Restauration prep partielle:', restored);
        }
      }
    }

    // Demander le nom du capitaine au tout premier lancement (1 seule fois,
    // persisté en localStorage). Réutilisé pour les bug reports + welcome.
    if (!PlayerProfile.hasPseudo()) {
      const chosen = await showUsernamePopup();
      PlayerProfile.setPseudo(chosen);
    }

    // Équipement starter à la première visite
    this.giveStarterEquipment(camp);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(-10);
    this.createAnimatedBackground();

    // Générer toutes les textures SVG (sprites + icônes)
    const loadingText = this.add.text(GAME_CX, 500, 'Chargement des sprites...', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    try {
      const renderer = new SpriteRenderer(this);
      await renderer.generateAllTextures();
    } catch (e) {
      console.warn('[BootScene] Erreur génération textures:', e);
    }
    loadingText.destroy();

    // Initialiser et jouer la musique du menu
    MusicManager.init();
    MusicManager.play(this, 'menu');

    // Logo SVG
    if (this.textures.exists('logo')) {
      this.add.image(GAME_CX, 140, 'logo').setOrigin(0.5);
    } else {
      this.add.text(GAME_CX, 140, '🏴‍☠️ DEADTIDE 🏴‍☠️', {
        fontSize: '28px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(0.5);
    }

    this.add.text(GAME_CX, 210, 'Roguelike d\'extraction — Archipel Maudit', {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // Pseudo du capitaine (welcome perso)
    const pseudo = PlayerProfile.getPseudo();
    if (pseudo) {
      this.add.text(GAME_CX, 230, `⚓ Capitaine ${pseudo}`, {
        fontSize: '12px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(0.5);
    }

    // Stats rapides (déplacé légèrement plus bas pour laisser place au pseudo)
    const stats = meta.getStats();
    if (stats.totalRuns > 0) {
      this.add.text(GAME_CX, 252, `${stats.totalRuns} expéditions  |  ${stats.runsWon} victoires  |  ${stats.enemiesKilled} kills`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5);
    }

    // === BOUTONS MENU (empilés verticalement) ===
    let btnY = 290;
    const btnSpacing = 42;

    // Bouton Reprendre (si run sauvegardé)
    if (RunManager.hasSavedRun()) {
      const resumeBtn = this.add.text(GAME_CX, btnY, '[ ⚔️ Reprendre l\'expédition ]', {
        fontSize: '16px', color: '#f39c12', fontFamily: FONT_BODY,
        backgroundColor: '#2a2a1a', padding: { x: 20, y: 8 },
      }).setOrigin(0.5);
      resumeBtn.setInteractive({ useHandCursor: true });
      resumeBtn.on('pointerdown', () => {
        const run = RunManager.loadSavedRun(meta, camp);
        if (run) {
          startSceneWithFade(this, 'MapScene', { run });
        } else {
          const campSceneKey = IslandCampFlag.isEnabled() ? 'IslandCampScene' : 'CampScene';
          startSceneWithFade(this, campSceneKey, { camp, meta });
        }
      });
      resumeBtn.on('pointerover', () => resumeBtn.setColor('#f1c40f'));
      resumeBtn.on('pointerout', () => resumeBtn.setColor('#f39c12'));
      addHoverPulse(this, resumeBtn);
      btnY += btnSpacing + 8;
    }

    // Bouton Jouer
    const playBtn = this.add.text(GAME_CX, btnY, '[ 🏴‍☠️ Entrer au Camp ]', {
      fontSize: '18px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 20, y: 8 },
    }).setOrigin(0.5);
    playBtn.setInteractive({ useHandCursor: true });
    playBtn.on('pointerdown', () => {
      RunManager.clearSavedRun();
      const campSceneKey = IslandCampFlag.isEnabled() ? 'IslandCampScene' : 'CampScene';
      startSceneWithFade(this, campSceneKey, { camp, meta, firstVisit: stats.totalRuns === 0 });
    });
    playBtn.on('pointerover', () => playBtn.setColor('#27ae60'));
    playBtn.on('pointerout', () => playBtn.setColor('#2ecc71'));
    addHoverPulse(this, playBtn, 1.07);
    btnY += btnSpacing + 8;

    // Bouton Stats
    this.addMenuButton(GAME_CX, btnY, '📊 Statistiques & Succès', '#3498db', () => {
      startSceneWithFade(this, 'StatsScene', { meta });
    });
    btnY += btnSpacing;

    // Bouton Classement en ligne
    this.addMenuButton(GAME_CX, btnY, '🏆 Classement en ligne', '#1abc9c', () => {
      startSceneWithFade(this, 'LeaderboardScene', { returnTo: 'BootScene' });
    });
    btnY += btnSpacing;

    // Bouton Codex
    this.addMenuButton(GAME_CX, btnY, '📖 Codex', '#e67e22', () => {
      startSceneWithFade(this, 'CodexScene', { returnTo: 'BootScene' });
    });
    btnY += btnSpacing;

    // Bouton Roadmap (carnet de bord) — Phase 10
    this.addMenuButton(GAME_CX, btnY, '📜 Roadmap', '#f1c40f', () => {
      startSceneWithFade(this, 'RoadmapScene', { returnTo: 'BootScene' });
    });
    btnY += btnSpacing;

    // Bouton Patchnotes — Phase 10
    this.addMenuButton(GAME_CX, btnY, '📋 Patchnotes', '#3498db', () => {
      startSceneWithFade(this, 'PatchNotesScene', { returnTo: 'BootScene' });
    });
    btnY += btnSpacing;

    // Bouton Paramètres
    this.addMenuButton(GAME_CX, btnY, '⚙️ Paramètres', '#7f8c8d', () => {
      startSceneWithFade(this, 'SettingsScene', { returnTo: 'BootScene' });
    });
    btnY += btnSpacing;

    // Bouton "Revoir les tutoriels" — efface les flags localStorage. Au
    // prochain passage dans la scène concernée (combat, carte, atelier…),
    // le tuto réapparaît comme à la première visite.
    this.addMenuButton(GAME_CX, btnY, '📚 Revoir les tutoriels', '#9b59b6', () => {
      resetAllTutorials();
      // Petit toast de confirmation (auto-disparait)
      const toast = this.add.text(GAME_CX, btnY + 30, '✅ Tutoriels réactivés', {
        fontSize: '11px', color: '#2ecc71', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(50);
      this.tweens.add({
        targets: toast, alpha: 0, duration: 1800, delay: 1200,
        onComplete: () => toast.destroy(),
      });
    });
    btnY += btnSpacing;

    // Bandeau bêta discret en bas
    this.add.text(GAME_CX, GAME_H - 22, '⚠️ Version bêta — Des bugs peuvent survenir. Signalez-les !', {
      fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
    }).setOrigin(0.5, 1);

    // Version discrète en bas à droite
    this.add.text(GAME_W - 10, GAME_H - 10, `v${GAME_VERSION}`, {
      fontSize: '10px', color: '#6c7a89', fontFamily: FONT_BODY,
    }).setOrigin(1, 1);

    // Popup de mise à jour si la sauvegarde date d'une version antérieure
    const showedMismatch = meta.hasSaveVersionMismatch();
    if (showedMismatch) {
      this.showVersionMismatchPopup(meta.getSavedVersion()!);
    }

    // Phase 10 — Popup "Quoi de neuf" au premier lancement après une mise à jour.
    // - Premier lancement absolu : on "claim" la version actuelle silencieusement
    //   (ainsi le futur upgrade déclenchera la popup correctement).
    // - Si la mismatch popup tourne déjà, on ack en silence pour éviter la pile.
    const lastSeen = getLastSeenVersion();
    if (lastSeen === null) {
      markPatchNotesAsSeen();
    } else if (lastSeen !== GAME_VERSION) {
      if (showedMismatch) {
        markPatchNotesAsSeen();
      } else {
        this.showWhatsNewPopup(lastSeen);
      }
    }

    // Sprint 81 — Konami Code (↑↑↓↓←→←→BA) → combat secret Captain Jack
    this.installKonamiCode();
  }

  /**
   * Capture la séquence du Konami Code depuis le menu principal.
   * Easter egg : déverrouille le combat 1v1 contre Captain Jack Warraps.
   */
  private installKonamiCode(): void {
    const SEQUENCE = ['UP', 'UP', 'DOWN', 'DOWN', 'LEFT', 'RIGHT', 'LEFT', 'RIGHT', 'B', 'A'];
    let progress = 0;
    if (!this.input.keyboard) return;
    this.input.keyboard.on('keydown', (event: KeyboardEvent) => {
      // Mapper KeyboardEvent.key vers nos labels
      const k = event.key.toUpperCase();
      let label: string | null = null;
      if (k === 'ARROWUP') label = 'UP';
      else if (k === 'ARROWDOWN') label = 'DOWN';
      else if (k === 'ARROWLEFT') label = 'LEFT';
      else if (k === 'ARROWRIGHT') label = 'RIGHT';
      else if (k === 'B') label = 'B';
      else if (k === 'A') label = 'A';

      if (label === SEQUENCE[progress]) {
        progress++;
        if (progress >= SEQUENCE.length) {
          progress = 0;
          this.launchJackDuel();
        }
      } else {
        // Reset, mais autoriser le démarrage d'une nouvelle séquence si la touche est la première
        progress = (label === SEQUENCE[0]) ? 1 : 0;
      }
    });
  }

  /**
   * Sprint 82 v3 — Lance le combat secret Captain Jack via le flux normal
   * (RunPrepScene → MapScene → CombatScene). Crée un RunManager dédié avec
   * une map à 1 seul nœud boss contenant Jack.
   */
  private launchJackDuel(): void {
    const meta = new MetaManager();
    const camp = new CampManager();
    RunManager.clearSavedRun();
    const run = RunManager.createJackDuelRun(meta, camp);
    // Lancer RunPrepScene qui prendra le run dédié
    startSceneWithFade(this, 'RunPrepScene', { camp, meta, presetRun: run });
  }

  /**
   * Affiche une popup informant le joueur qu'une nouvelle version est disponible.
   * Propose de réinitialiser l'aventure ou de continuer avec la sauvegarde existante.
   */
  private showVersionMismatchPopup(savedVersion: string): void {
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.75)
      .setDepth(150).setInteractive();

    const boxW = Math.min(330, GAME_W - 30);
    const _mob = GAME_W < 500;
    const btnSpacing = _mob ? 70 : 80;
    const box = this.add.rectangle(GAME_CX, GAME_CY, boxW, 230, 0x1a1a2e)
      .setDepth(151).setStrokeStyle(2, 0xf39c12);

    const title = this.add.text(GAME_CX, GAME_CY - 90, '🆕 Nouvelle version disponible', {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(152);

    const desc = this.add.text(GAME_CX, GAME_CY - 52,
      `Votre sauvegarde date de la v${savedVersion}.\nLe jeu est maintenant en v${GAME_VERSION}.`, {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY, align: 'center',
    }).setOrigin(0.5).setDepth(152);

    const question = this.add.text(GAME_CX, GAME_CY - 5,
      'Réinitialiser l\'aventure pour profiter\nau mieux de la nouvelle expérience ?', {
      fontSize: '11px', color: '#ecf0f1', fontFamily: FONT_BODY, align: 'center',
    }).setOrigin(0.5).setDepth(152);

    const resetBtn = this.add.text(GAME_CX - btnSpacing, GAME_CY + 70, '[ 🗑️ Réinitialiser ]', {
      fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(152).setInteractive({ useHandCursor: true });

    const continueBtn = this.add.text(GAME_CX + btnSpacing, GAME_CY + 70, '[ ✅ Continuer ]', {
      fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(152).setInteractive({ useHandCursor: true });

    const elements = [overlay, box, title, desc, question, resetBtn, continueBtn];
    const close = () => elements.forEach(e => e.destroy());

    resetBtn.on('pointerover', () => resetBtn.setColor('#ff6b6b'));
    resetBtn.on('pointerout', () => resetBtn.setColor('#e74c3c'));
    resetBtn.on('pointerdown', () => {
      // Effacer toutes les données de jeu (hors préférences musique)
      try {
        for (const key of RESET_KEYS) localStorage.removeItem(key);
      } catch { /* localStorage indisponible */ }
      this.scene.restart();
    });

    continueBtn.on('pointerover', () => continueBtn.setColor('#27ae60'));
    continueBtn.on('pointerout', () => continueBtn.setColor('#2ecc71'));
    continueBtn.on('pointerdown', close);
  }

  /**
   * Phase 10 — Popup "Quoi de neuf" au premier lancement après une mise à jour.
   *
   * v0.40.4 : agrège **toutes les versions ratées** depuis `previousVersion`,
   * pas seulement la dernière. Un joueur 15 versions en retard voit chaque
   * version manquée par une ligne (emoji catégorie dominante + numéro + headline),
   * avec un point d'orgue sur la plus récente (3 entries détaillées).
   *
   * Calcul des versions ratées :
   *   - `PATCH_NOTES` est trié newest-first manuellement.
   *   - On prend tout ce qui précède (index plus petit = plus récent) la
   *     `previousVersion`. Si elle n'apparaît pas (version très vieille / pré-popup) →
   *     on affiche tout l'historique.
   *   - Cap à 8 versions pour éviter le mur de texte ; affichage du surplus en
   *     "... et X autres versions" à la fin.
   *
   * Cliquer "Voir le détail" ouvre PatchNotesScene. "Plus tard" ack silencieusement.
   */
  private showWhatsNewPopup(previousVersion: string): void {
    // === 1. CALCUL DES VERSIONS RATÉES ===
    const missed = this.computeMissedVersions(previousVersion);
    const missedCount = missed.length;
    if (missedCount === 0) {
      // Edge case : la version "ratée" n'a aucune entrée associée (rare).
      // On ne montre rien plutôt qu'une popup vide.
      markPatchNotesAsSeen();
      return;
    }
    const MAX_LIST = 8;
    const listed = missed.slice(0, MAX_LIST);
    const overflow = Math.max(0, missedCount - MAX_LIST);
    const latest = missed[0]; // version la plus récente parmi les ratées

    // === 2. HAUTEUR DYNAMIQUE DU PANNEAU ===
    // Plus de versions = plus haut, plafonné par GAME_H.
    const baseH = 200;                                  // titres + boutons + paddings
    const perVersionH = 28;                             // ~1 ligne + marge par version listée
    const teaserH = 80;                                 // 3 lignes du détail de la plus récente
    const dynamicH = baseH + listed.length * perVersionH + teaserH + (overflow > 0 ? 14 : 0);
    const boxH = Math.min(GAME_H - 30, dynamicH);
    const boxW = Math.min(380, GAME_W - 30);
    const topY = GAME_CY - boxH / 2;
    const bottomY = GAME_CY + boxH / 2;

    // === 3. RENDU ===
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.75)
      .setDepth(150).setInteractive();

    const box = this.add.rectangle(GAME_CX, GAME_CY, boxW, boxH, 0x1a1a2e)
      .setDepth(151).setStrokeStyle(2, 0x3498db);

    const titleText = missedCount === 1
      ? '🎉 Nouvelle version disponible'
      : `🎉 ${missedCount} versions à découvrir`;
    const title = this.add.text(GAME_CX, topY + 20, titleText, {
      fontSize: '14px', color: '#3498db', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(152);

    const versionInfo = this.add.text(GAME_CX, topY + 42,
      `v${previousVersion}  →  v${GAME_VERSION}`, {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(152);

    // === 4. LISTE DES VERSIONS (1 ligne chacune avec headline) ===
    let y = topY + 70;
    const elementsAccum: Phaser.GameObjects.GameObject[] = [overlay, box, title, versionInfo];
    for (let i = 0; i < listed.length; i++) {
      const v = listed[i];
      const isLatest = i === 0;
      // Emoji catégorie dominante (préférence new > improvement > fix)
      const hasNew = v.entries.some(e => e.kind === 'new');
      const hasImp = v.entries.some(e => e.kind === 'improvement');
      const emoji = hasNew ? '🆕' : hasImp ? '✨' : '🛠️';
      const star = isLatest ? '⭐ ' : '   ';
      const line = `${star}${emoji} v${v.version.replace('-beta', '')} — ${v.headline ?? '(détails dans les notes)'}`;
      const lineText = this.add.text(GAME_CX, y, line, {
        fontSize: '10px',
        color: isLatest ? '#f1c40f' : '#ecf0f1',
        fontFamily: FONT_BODY,
        fontStyle: isLatest ? 'bold' : 'normal',
        align: 'center',
        wordWrap: { width: boxW - 30 },
      }).setOrigin(0.5, 0).setDepth(152);
      elementsAccum.push(lineText);
      y += Math.max(perVersionH, lineText.height + 6);
    }
    if (overflow > 0) {
      const moreText = this.add.text(GAME_CX, y, `… et ${overflow} autre${overflow > 1 ? 's' : ''} version${overflow > 1 ? 's' : ''}`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'italic',
      }).setOrigin(0.5, 0).setDepth(152);
      elementsAccum.push(moreText);
      y += 16;
    }

    // === 5. APERÇU DÉTAILLÉ DE LA PLUS RÉCENTE ===
    const separator = this.add.rectangle(GAME_CX, y + 6, boxW - 60, 1, 0x3498db, 0.5).setDepth(152);
    elementsAccum.push(separator);
    y += 14;

    const teaserTitle = this.add.text(GAME_CX, y, `✨ Aperçu de v${latest.version}`, {
      fontSize: '10px', color: '#3498db', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5, 0).setDepth(152);
    elementsAccum.push(teaserTitle);
    y += 16;

    // Top 3 entries de la plus récente, priorité new → improvement → fix
    const topEntries = [...latest.entries].sort((a, b) => {
      const w = { new: 0, improvement: 1, fix: 2 } as const;
      return w[a.kind] - w[b.kind];
    }).slice(0, 3);
    const teaser = topEntries.map(e => {
      const em = e.kind === 'new' ? '🆕' : e.kind === 'fix' ? '🛠️' : '✨';
      return `${em} ${e.text}`;
    }).join('\n');
    const teaserText = this.add.text(GAME_CX, y, teaser, {
      fontSize: '10px', color: '#ecf0f1', fontFamily: FONT_BODY,
      align: 'center', wordWrap: { width: boxW - 30 },
    }).setOrigin(0.5, 0).setDepth(152);
    elementsAccum.push(teaserText);

    // === 6. BOUTONS ===
    const btnY = bottomY - 30;
    const seeBtn = this.add.text(GAME_CX - 80, btnY, '[ 📋 Voir le détail ]', {
      fontSize: '12px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a2438', padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(152).setInteractive({ useHandCursor: true });

    const laterBtn = this.add.text(GAME_CX + 80, btnY, '[ ✖️ Plus tard ]', {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a1a', padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(152).setInteractive({ useHandCursor: true });

    elementsAccum.push(seeBtn, laterBtn);
    const close = () => elementsAccum.forEach(e => e.destroy());

    seeBtn.on('pointerover', () => seeBtn.setColor('#5dade2'));
    seeBtn.on('pointerout', () => seeBtn.setColor('#3498db'));
    seeBtn.on('pointerdown', () => {
      // PatchNotesScene marque la version comme vue dans son create()
      close();
      startSceneWithFade(this, 'PatchNotesScene', { returnTo: 'BootScene' });
    });

    laterBtn.on('pointerover', () => laterBtn.setColor('#bdc3c7'));
    laterBtn.on('pointerout', () => laterBtn.setColor('#7f8c8d'));
    laterBtn.on('pointerdown', () => {
      // Ack silencieux : on ne reposera pas la question au prochain lancement
      markPatchNotesAsSeen();
      close();
    });
  }

  /**
   * Renvoie les versions de `PATCH_NOTES` situées *après* `previousVersion`
   * (donc plus récentes — soit en tête du tableau puisque trié newest-first).
   * Si `previousVersion` est absente du tableau (joueur sur une version très
   * vieille jamais documentée), on renvoie tout l'historique.
   */
  private computeMissedVersions(previousVersion: string): typeof PATCH_NOTES {
    const idx = PATCH_NOTES.findIndex(p => p.version === previousVersion);
    if (idx < 0) return [...PATCH_NOTES];
    return PATCH_NOTES.slice(0, idx);
  }

  /**
   * Décor animé du menu : vagues qui défilent horizontalement, drapeau pirate
   * qui se balance, étoiles qui scintillent. Pas d'impact perf majeur (tweens légers).
   */
  private createAnimatedBackground(): void {
    // Étoiles scintillantes en haut (ciel nocturne)
    for (let i = 0; i < 18; i++) {
      const x = Phaser.Math.Between(10, GAME_W - 10);
      const y = Phaser.Math.Between(10, 110);
      const star = this.add.circle(x, y, Phaser.Math.Between(1, 2), 0xffffff, 0.6);
      this.tweens.add({
        targets: star,
        alpha: 0.15,
        duration: Phaser.Math.Between(800, 1800),
        yoyo: true,
        repeat: -1,
        delay: Phaser.Math.Between(0, 2000),
      });
    }

    // Mer nocturne : ligne d'horizon + 3 couches de vagues sinusoïdales en parallax.
    // Chaque couche est un Graphics rempli, animé via le ticker de la scène.
    const horizonY = GAME_H * 0.58;

    // Lune en haut-droite (hors de la colonne de boutons centrale)
    const moonX = GAME_W * 0.82;
    const moonY = horizonY - 80;
    // Halo extérieur (large, faible alpha)
    const moonGlow = this.add.circle(moonX, moonY, 32, 0xf5d77a, 0.10).setDepth(-7);
    this.tweens.add({
      targets: moonGlow,
      alpha: 0.20,
      duration: 2400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    // Disque de la lune
    this.add.circle(moonX, moonY, 12, 0xf8e9b5, 0.85).setDepth(-6);
    this.add.circle(moonX, moonY, 9, 0xfff3c8, 0.55).setDepth(-6);

    // Reflet vertical sur l'eau, sous la lune (s'estompe vers le bas)
    for (let i = 0; i < 6; i++) {
      const y = horizonY + 6 + i * 14;
      const w = 14 - i * 1.5;
      const reflect = this.add.rectangle(moonX, y, w, 4, 0xf5d77a, 0.18 - i * 0.025).setDepth(-4);
      this.tweens.add({
        targets: reflect,
        alpha: (0.18 - i * 0.025) * 0.4,
        duration: 1400 + i * 200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: i * 100,
      });
    }

    // Ligne d'horizon discrète
    const horizon = this.add.rectangle(GAME_CX, horizonY, GAME_W, 1, 0x4a6580, 0.35).setDepth(-5);
    horizon.setOrigin(0.5);

    // 3 couches de vagues : la plus lointaine plus claire et plus calme,
    // la plus proche plus sombre et plus marquée (illusion de profondeur).
    const waveLayers = [
      { y: horizonY + 18, amp: 2.5, length: 60, color: 0x2a4a70, alpha: 0.55, speed: 0.45, phase: 0 },
      { y: horizonY + 50, amp: 4.5, length: 90, color: 0x1a3458, alpha: 0.75, speed: 0.65, phase: 1.3 },
      { y: horizonY + 95, amp: 7.0, length: 130, color: 0x0e2040, alpha: 0.92, speed: 0.85, phase: 2.7 },
    ];
    const waveGfx = waveLayers.map(() => this.add.graphics().setDepth(-4));

    // Crêtes d'écume sur la couche du fond (petits points clairs sur les sommets)
    const foamCount = 12;
    const foamDots: Phaser.GameObjects.Arc[] = [];
    for (let i = 0; i < foamCount; i++) {
      const dot = this.add.circle(0, 0, 1.2, 0xc8d8e8, 0.65).setDepth(-3);
      foamDots.push(dot);
    }

    // Animation : on redessine les courbes à chaque frame en faisant glisser la phase.
    // Coût modeste (~3 × GAME_W/4 = ~340 segments par frame).
    this.events.on('update', (_t: number, delta: number) => {
      const dt = delta * 0.001;
      for (let i = 0; i < waveLayers.length; i++) {
        const layer = waveLayers[i];
        layer.phase += dt * layer.speed;
        const g = waveGfx[i];
        g.clear();
        g.fillStyle(layer.color, layer.alpha);
        g.beginPath();
        g.moveTo(0, GAME_H);
        const step = 4;
        for (let x = 0; x <= GAME_W; x += step) {
          // Double sinusoïde pour casser la régularité (vraie houle)
          const y = layer.y
            + Math.sin(x / layer.length + layer.phase) * layer.amp
            + Math.sin(x / (layer.length * 0.45) + layer.phase * 1.7) * layer.amp * 0.35;
          g.lineTo(x, y);
        }
        g.lineTo(GAME_W, GAME_H);
        g.closePath();
        g.fillPath();
      }

      // Écume : on positionne les dots sur les crêtes de la couche du milieu
      const foamLayer = waveLayers[1];
      for (let i = 0; i < foamCount; i++) {
        const baseX = (i / foamCount) * GAME_W + (foamLayer.phase * 18) % (GAME_W / foamCount);
        const x = baseX % GAME_W;
        const y = foamLayer.y
          + Math.sin(x / foamLayer.length + foamLayer.phase) * foamLayer.amp
          + Math.sin(x / (foamLayer.length * 0.45) + foamLayer.phase * 1.7) * foamLayer.amp * 0.35;
        foamDots[i].setPosition(x, y - 1);
        // Apparition seulement près des crêtes (sin proche de +1)
        const crestStrength = (Math.sin(x / foamLayer.length + foamLayer.phase) + 1) / 2;
        foamDots[i].setAlpha(crestStrength * 0.7);
      }
    });

    // Drapeau pirate en haut à droite qui se balance
    const flag = this.add.text(GAME_W - 24, 36, '🏴‍☠️', {
      fontSize: '22px',
    }).setOrigin(0.5);
    flag.setAngle(-8);
    this.tweens.add({
      targets: flag,
      angle: 8,
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Crée un bouton de menu standardisé */
  private addMenuButton(x: number, y: number, label: string, color: string, onClick: () => void): void {
    const btn = this.add.text(x, y, label, {
      fontSize: '13px', color, fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 16, y: 7 },
    }).setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', onClick);
    btn.on('pointerover', () => btn.setColor('#ecf0f1'));
    btn.on('pointerout', () => btn.setColor(color));
    addHoverPulse(this, btn);
  }

  /** Offre un équipement de départ à la première visite */
  private giveStarterEquipment(camp: CampManager): void {
    try {
      if (localStorage.getItem(STARTER_KEY)) return;
      // Coutelas Rouillé + Vieilles Bottes + Potion de Rhum
      camp.addItemToStorage('cutlass');
      camp.addItemToStorage('old_boots');
      camp.addConsumable('health_potion');
      localStorage.setItem(STARTER_KEY, '1');
    } catch {
      // localStorage indisponible
    }
  }
}
